import React, { useState } from "react";
import swal from "sweetalert";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
  TextField,
} from "@mui/material";
import { FilterBar, ReportHeader } from "./ReportsPages";
import { getAuditComplianceLogs } from "../../services/reportService";
import { useTranslation } from "react-i18next";

export function AuditComplianceReport() {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ from: "", to: "", group: "", search: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async (currentPage = 1) => {
    setLoading(true);
    setError(null);
    if (currentPage === 1) {
      setReportData(null);
      setPage(0);
    }

    try {
      const params = {
        start_date: filters.from,
        end_date: filters.to,
        group_id: filters.group,
        search: filters.search,
        page: currentPage,
        page_size: rowsPerPage,
      };
      const data = await getAuditComplianceLogs(params);
      setReportData(data);
    } catch (err) {
      setError(err.message);
      swal(t("Error"), err.message, "error");
    } finally {
      setLoading(false);
    }
  };
  const handleExport = () => swal(t("Info"), t("Export logic not yet implemented."), "info");

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    handleGenerate(newPage + 1); // API is 1-indexed
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const extraFilters = (
    <TextField
      label={t("User / Action Search")}
      size="small"
      fullWidth value={filters.search || ""} onChange={(e) => handleFilterChange("search", e.target.value)}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <ReportHeader
        title={t("Audit & Compliance Report")}
        subtitle={t("Trace all system actions for regulatory and internal audit requirements.")}
        roleHint={t("Access: Admin only (or as per policy)")}
      />

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onGenerate={() => handleGenerate(1)}
        isGenerating={loading}
        onExport={handleExport}
        showGroup={true}
        extraFilters={extraFilters}
      />

      {loading && <Typography>{t("Loading report...")}</Typography>}
      {error && <Typography color="error">{t("Error")}: {error}</Typography>}

      {!reportData && !loading && (
        <Typography color="text.secondary" align="center" sx={{ my: 4 }}>
          {t("Please select filters and click \"Generate\" to view the report.")}
        </Typography>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            {t("Audit Trail")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Date & Time")}</TableCell>
                  <TableCell>{t("User")}</TableCell>
                  <TableCell>{t("Action")}</TableCell>
                  <TableCell>{t("Details")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData?.results?.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell>
                    <TableCell>{r.user}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.action}
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>{r.details}</TableCell>
                  </TableRow>
                ))}
                {reportData && reportData?.results?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("No matching audit events for the selected filters.")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          {reportData && (
            <TablePagination
              component="div"
              count={reportData.count || 0}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
